"use client";

import { useState, useTransition } from "react";
import { deleteMedia, updateMedia } from "../actions";
import type { MediaItem } from "../types";
import { uploadFile } from "./media";
import { BTN_GHOST, BTN_PRIMARY, ConfirmButton, INPUT, Notice, PageTitle } from "./ui";

const size = (bytes: number) => (bytes > 1024 * 1024 ? `${(bytes / 1024 / 1024).toFixed(1)} MB` : `${Math.round(bytes / 1024)} KB`);

/** The media library: upload, browse, describe and delete images and videos. */
export function MediaLibrary({ media }: { media: MediaItem[] }) {
  const [items, setItems] = useState(media);
  const [filter, setFilter] = useState<"all" | "image" | "video">("all");
  const [query, setQuery] = useState("");
  const [uploading, setUploading] = useState(0);
  const [over, setOver] = useState(false);
  const [notice, setNotice] = useState<{ type: "ok" | "error"; text: string } | null>(null);
  const [, startTransition] = useTransition();

  const upload = async (files: FileList | File[]) => {
    const list = Array.from(files);
    if (!list.length) return;
    setUploading(list.length);
    const errors: string[] = [];
    for (const file of list) {
      const { item, error } = await uploadFile(file);
      if (item) setItems((current) => [item, ...current]);
      if (error) errors.push(`${file.name}: ${error}`);
      setUploading((n) => n - 1);
    }
    setNotice(errors.length ? { type: "error", text: errors.join(" ") } : { type: "ok", text: `Uploaded ${list.length} file${list.length > 1 ? "s" : ""}. Add a description to each.` });
  };

  const saveField = (id: string, changes: { alt?: string; name?: string }) =>
    startTransition(async () => {
      const result = await updateMedia(id, changes);
      setNotice(result.ok ? { type: "ok", text: "Saved." } : { type: "error", text: result.error });
    });

  const remove = (id: string) =>
    startTransition(async () => {
      const result = await deleteMedia(id);
      if (result.ok) setItems((current) => current.filter((m) => m.id !== id));
      setNotice(result.ok ? { type: "ok", text: "Deleted." } : { type: "error", text: result.error });
    });

  const shown = items.filter((m) => (filter === "all" || m.kind === filter) && (m.name + " " + m.alt).toLowerCase().includes(query.toLowerCase()));

  return (
    <div>
      <PageTitle title="Media">
        <label className={`${BTN_PRIMARY} cursor-pointer`}>
          {uploading ? `Uploading ${uploading}…` : "Upload"}
          <input type="file" multiple accept="image/jpeg,image/png,image/webp,image/avif,image/gif,video/mp4,video/webm" className="sr-only" onChange={(e) => e.target.files && upload(e.target.files)} />
        </label>
      </PageTitle>

      <div
        onDragOver={(e) => {
          e.preventDefault();
          setOver(true);
        }}
        onDragLeave={() => setOver(false)}
        onDrop={(e) => {
          e.preventDefault();
          setOver(false);
          upload(e.dataTransfer.files);
        }}
        className={`mb-6 hidden rounded-2xl border-2 border-dashed p-6 text-center text-midnight/70 md:block ${over ? "border-teal bg-teal/5" : "border-midnight/20"}`}
      >
        Drop images or videos here. Images up to 10 MB (JPG, PNG, WebP, AVIF, GIF), videos up to 25 MB (MP4, WebM).
      </div>

      <div className="mb-4 flex flex-wrap items-center gap-2">
        {(["all", "image", "video"] as const).map((f) => (
          <button key={f} type="button" aria-pressed={filter === f} onClick={() => setFilter(f)} className={`${BTN_GHOST} ${filter === f ? "border-midnight bg-midnight text-offwhite hover:text-offwhite" : ""}`}>
            {f === "all" ? "All" : f === "image" ? "Images" : "Videos"}
          </button>
        ))}
        <input type="search" aria-label="Search media" placeholder="Search…" value={query} onChange={(e) => setQuery(e.target.value)} className={`${INPUT} min-w-0 flex-1 basis-40 sm:max-w-xs`} />
      </div>

      <Notice notice={notice} />

      {shown.length ? (
        <ul className="mt-4 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {shown.map((m) => (
            <li key={m.id} className="flex flex-col overflow-hidden rounded-2xl border border-midnight/10 bg-sand">
              {m.kind === "image" ? (
                <img src={m.url} alt={m.alt || m.name} loading="lazy" className="aspect-[16/10] w-full bg-offwhite object-cover" />
              ) : (
                <video src={m.url} controls preload="metadata" className="aspect-[16/10] w-full bg-ink" />
              )}
              <div className="flex flex-1 flex-col gap-2 p-3">
                <input aria-label="Name" defaultValue={m.name} onBlur={(e) => e.target.value !== m.name && saveField(m.id, { name: e.target.value })} className={`${INPUT} font-bold`} />
                <input
                  aria-label="Description (alt text)"
                  placeholder={m.kind === "image" ? "Describe the image (alt text)" : "Describe the video"}
                  defaultValue={m.alt}
                  onBlur={(e) => e.target.value !== m.alt && saveField(m.id, { alt: e.target.value })}
                  className={`${INPUT} ${!m.alt ? "border-amber" : ""}`}
                />
                <p className="font-mono text-xs text-midnight/70">
                  {m.kind} · {size(m.size)} · {new Date(m.uploadedAt).toLocaleDateString("en-GB")}
                </p>
                <div className="mt-auto flex flex-wrap gap-2 pt-1">
                  <button
                    type="button"
                    onClick={() => {
                      navigator.clipboard?.writeText(location.origin + m.url);
                      setNotice({ type: "ok", text: "Link copied." });
                    }}
                    className={BTN_GHOST}
                  >
                    Copy link
                  </button>
                  <ConfirmButton question="Delete this file?" onConfirm={() => remove(m.id)} />
                </div>
              </div>
            </li>
          ))}
        </ul>
      ) : (
        <p className="mt-4 rounded-2xl border border-dashed border-midnight/20 p-8 text-center text-midnight/70">{items.length ? "Nothing matches." : "No media yet. Upload images and videos to use them on the website."}</p>
      )}
    </div>
  );
}
